import { createSelector } from 'reselect';
import { paths, toLower, sortBy, compose } from 'ramda';

import { GUIDE_TYPE } from 'yeself-common/ModelConstant';
import { RULE_TYPE } from 'yeself-common/enum/RuleEnum';

import { DEFAULT_CONSOLE_LANGUAGE_CODE } from 'Console/constants/appConstants';
import { selectConsoleLanguageCode, selectContentLanguageCode } from 'Console/data/language/selectors';

const getGuides = (state) => state.applicationDetail.guides;
const getGuideById = (state, guideId) =>
    state.applicationDetail.guides.data.find((item) => item.id === guideId);

export const makeSelectGuideType = () => createSelector([getGuideById], ({ type }) => type);

export const makeSelectGuideGraph = () => createSelector([getGuideById], ({ graph }) => graph);

export const makeSelectGraphMetadata = () =>
    createSelector([makeSelectGuideGraph()], ({ metadata }) => metadata);

export const makeSelectGraphSandbox = () =>
    createSelector([makeSelectGraphMetadata()], ({ sandbox = false }) => sandbox);

export const makeSelectShouldGuideStartAutomatically = () =>
    createSelector([makeSelectGraphMetadata()], ({ rulesDefinition }) => {
        const { identity, visitor } = rulesDefinition;
        const predicate = (rule) => rule.type === RULE_TYPE.DO_NOT_START_AUTOMATICALLY;

        return !(identity.find(predicate) || visitor.find(predicate));
    });

export const selectGuidesSearchTerm = createSelector([getGuides], ({ searchTerm = '' }) => searchTerm);

export const selectApplicationGuidesList = createSelector([getGuides], ({ data = [] }) => data);

export const selectSortedApplicationGuidesList = createSelector(
    [getGuides, selectContentLanguageCode],
    ({ data = [] }, contentLanguage = DEFAULT_CONSOLE_LANGUAGE_CODE) => {
        const guideLanguageName = (guide) =>
            paths(
                [
                    ['name', contentLanguage],
                    ['name', DEFAULT_CONSOLE_LANGUAGE_CODE],
                ],
                guide
            ).find((name) => name) || '';
        const contentLanguageGuides = compose(toLower, guideLanguageName);
        const sortByName = sortBy(contentLanguageGuides);

        return sortByName(data);
    }
);

export const selectApplicationTours = createSelector([selectApplicationGuidesList], (guides = []) =>
    guides.filter((guide) => guide.type === GUIDE_TYPE.TOUR)
);

export const selectGuideFilterOptions = createSelector(
    [selectContentLanguageCode, selectApplicationGuidesList],
    (contentLanguage = DEFAULT_CONSOLE_LANGUAGE_CODE, guides = []) =>
        guides.map((guide) => ({
            id: guide.id,
            name:
                guide.name[contentLanguage.code] ||
                guide.name[DEFAULT_CONSOLE_LANGUAGE_CODE] ||
                Object.values(guide.name)[0] ||
                'Missing tour name',
        }))
);

export const selectApplicationGuidesAreLoading = createSelector(
    [getGuides],
    ({ isLoading = false }) => isLoading
);

export const selectApplicationGuidesHasError = createSelector(
    [getGuides],
    ({ hasError = false }) => hasError
);

export const selectRuleAttributes = createSelector(
    [getGuides],
    ({ rulesAttribute = {} }) => rulesAttribute
);

export const selectRuleAttributesData = createSelector([selectRuleAttributes], ({ data = [] }) => data);

export const selectApplicationGuideNames = createSelector(
    [selectApplicationGuidesList, selectConsoleLanguageCode, (state, currentGuideId) => currentGuideId],
    (guidesList = {}, locale, currentGuideId) => {
        const filteredGuides = Object.values(guidesList).filter((guide) => guide.id !== currentGuideId);

        const guidesWithLocaleNames = filteredGuides.map((guide) => ({
            value: guide.id,
            label: guide.name[locale] || '',
        }));

        guidesWithLocaleNames.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

        return guidesWithLocaleNames;
    }
);

export const selectAttributeNames = createSelector(
    [selectRuleAttributesData, selectConsoleLanguageCode],
    (data, locale) =>
        data.map((item) => ({
            value: item.attribute_definition.system_key,
            label:
                item.attribute_definition.configuration && item.attribute_definition.configuration.label
                    ? item.attribute_definition.configuration.label[locale]
                    : item.attribute_definition.system_key,
        }))
);

export const selectInteractionCollectorNodeNames = createSelector(
    [selectApplicationGuidesList, selectConsoleLanguageCode, (state, currentGuideId) => currentGuideId],
    (guidesList = {}, locale, currentGuideId) => {
        const interactionCollectorGuides = Object.values(guidesList).filter(
            (guide) => guide.type === GUIDE_TYPE.INTERACTION_COLLECTOR && guide.id !== currentGuideId
        );

        return interactionCollectorGuides.map((guide) => ({
            value: guide.id,
            label: guide.name[locale] || '',
            options: guide.graph.nodes.map(({ id, metadata }) => ({
                value: id,
                label: metadata.index.toString(),
            })),
        }));
    }
);

export const selectContextOptions = createSelector(
    [getGuides],
    ({ rulesContextOptions = { data: {} } }) => rulesContextOptions.data
);
